import { useState, useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import Login from './Login';
import AIAgent from './AIAgent';
import KnowledgeBase from './KnowledgeBase';
import Conversations from './Conversations';
import Leads from './Leads';
import Settings from './Settings';
import Channels from './Channels';
import SuperAdmin from './SuperAdmin';

// ─── tiny helpers ────────────────────────────────────────────────────────────

const avatarColors = ["bg-orange-700", "bg-orange-800", "bg-stone-600"];

function Avatar({ label, colorClass = "bg-orange-700" }) {
  return (
    <div
      className={`w-5 h-5 rounded-full ${colorClass} border border-orange-500/40
        flex items-center justify-center text-white -ml-1.5 first:ml-0 flex-shrink-0`}
      style={{ fontSize: 7 }}
    >
      {label}
    </div>
  );
}

function DocThumb() {
  return (
    <div className="w-[72px] h-[90px] bg-white rounded-lg flex flex-col gap-1 p-2 flex-shrink-0 shadow">
      {[100, 75, 45, 90, 60, 80, 50].map((w, i) => (
        <div
          key={i}
          className={`h-[2px] rounded-full ${i % 2 === 0 ? "bg-gray-300" : "bg-gray-200"}`}
          style={{ width: `${w}%` }}
        />
      ))}
    </div>
  );
}

function DotMatrix({ cols = 8 }) {
  const p = ["high","mid","high","low","mid","high","low","low",
             "mid","low","high","mid","low","high","mid","low"];
  const c = (l) =>
    l === "high" ? "bg-orange-500" : l === "mid" ? "bg-orange-900" : "bg-stone-800";
  return (
    <div className={`grid gap-[3px]`} style={{ gridTemplateColumns: `repeat(${cols}, 1fr)` }}>
      {p.map((l, i) => (
        <div key={i} className={`w-2 h-2 rounded-sm ${c(l)}`} />
      ))}
    </div>
  );
}

// ─── MOBILE BOTTOM NAV ───────────────────────────────────────────────────────

function MobileBottomNav() {
  const navigate = useNavigate();
  const location = useLocation();
  const items = [
    { icon: "ti-layout-dashboard", label: "Home",     path: "/dashboard"     },
    { icon: "ti-robot",            label: "VOX",       path: "/ai-agent"      },
    { icon: "ti-message-dots",     label: "Chats",     path: "/conversations" },
    { icon: "ti-users",            label: "Leads",     path: "/leads"         },
    { icon: "ti-plug",             label: "Channels",  path: "/channels"      },
  ];
  return (
    <nav
      className="fixed bottom-0 left-0 right-0 z-50 flex items-center justify-around border-t lg:hidden"
      style={{ background: "#161412", borderColor: "#2a2825", paddingBottom: "env(safe-area-inset-bottom)" }}
    >
      {items.map(({ icon, label, path }) => {
        const active = location.pathname === path;
        return (
          <button
            key={path}
            onClick={() => navigate(path)}
            className="flex flex-col items-center gap-0.5 py-2.5 px-3 flex-1 transition-all"
            style={{ color: active ? "#e8602a" : "#6b6560" }}
          >
            <i className={`ti ${icon} text-lg`} />
            <span style={{ fontSize: 9, fontWeight: active ? 700 : 500 }}>{label}</span>
          </button>
        );
      })}
    </nav>
  );
}

// ─── MOBILE DRAWER MENU ──────────────────────────────────────────────────────

function MobileDrawer({ open, onClose }) {
  const navigate = useNavigate();
  const location = useLocation();
  const navItems = [
    { section: "OVERVIEW",  items: [{ icon: "ti-layout-dashboard", label: "Dashboard",    path: "/dashboard"     }] },
    { section: "ENGAGE",    items: [
        { icon: "ti-robot",        label: "Nigella VOX",   path: "/ai-agent"      },
        { icon: "ti-message-dots", label: "Conversations", path: "/conversations" },
        { icon: "ti-users",        label: "Leads",         path: "/leads"         },
    ]},
    { section: "CONFIGURE", items: [
        { icon: "ti-book",     label: "Knowledge Base", path: "/knowledge" },
        { icon: "ti-plug",     label: "Channels",       path: "/channels"  },
        { icon: "ti-settings", label: "Settings",       path: "/settings"  },
    ]},
  ];

  if (!open) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-40 lg:hidden"
        style={{ background: "rgba(0,0,0,0.7)" }}
        onClick={onClose}
      />
      {/* Drawer */}
      <div
        className="fixed top-0 left-0 bottom-0 z-50 flex flex-col lg:hidden"
        style={{ width: 260, background: "#161412", borderRight: "1px solid #2a2825" }}
      >
        {/* Brand */}
        <div className="flex items-center justify-between px-5 py-5 border-b" style={{ borderColor: "#2a2825" }}>
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-orange-600 rounded-xl flex items-center justify-center flex-shrink-0">
              <i className="ti ti-bolt text-white text-base" />
            </div>
            <div>
              <div className="text-sm font-bold text-stone-100 leading-tight">Nigella VOX</div>
              <div className="text-[9px] text-stone-500 uppercase tracking-widest">Agent Console</div>
            </div>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-xl flex items-center justify-center text-stone-500" style={{ background: "#252320" }}>
            <i className="ti ti-x text-sm" />
          </button>
        </div>

        {/* Nav */}
        <nav className="flex flex-col gap-5 px-3 py-5 flex-1 overflow-y-auto">
          {navItems.map(({ section, items }) => (
            <div key={section}>
              <div className="text-[9px] text-stone-600 uppercase tracking-widest mb-2 px-2">{section}</div>
              {items.map(({ icon, label, path }) => {
                const isActive = location.pathname === path;
                return (
                  <div
                    key={label}
                    onClick={() => { navigate(path); onClose(); }}
                    className={`flex items-center gap-3 px-3 py-3 rounded-xl cursor-pointer mb-0.5 transition-all
                      ${isActive
                        ? "bg-orange-600/20 text-orange-400 border border-orange-600/30"
                        : "text-stone-400 hover:text-stone-200 hover:bg-white/5"}`}
                  >
                    <i className={`ti ${icon} text-base flex-shrink-0`} />
                    <span className="text-sm font-medium">{label}</span>
                  </div>
                );
              })}
            </div>
          ))}
        </nav>

        {/* Footer */}
        <div className="px-5 pb-6 border-t pt-4" style={{ borderColor: "#2a2825" }}>
          <div className="flex items-center gap-1.5 text-[9px] text-stone-600 mb-1">
            <i className="ti ti-shield-check text-orange-700" />
            Secured workspace
          </div>
          <div className="text-[9px] text-stone-700">
            © 2026{" "}
            <a 
              href="https://awktechz.com/" 
              target="_blank" 
              rel="noopener noreferrer"
              className="hover:text-orange-500 transition-colors"
            >
              AWK Technologies
            </a>
          </div>
        </div>
      </div>
    </>
  );
}

// ─── LEFT SIDEBAR (desktop only) ─────────────────────────────────────────────

function Sidebar() {
  const navigate = useNavigate();
  const location = useLocation();
  const navItems = [
    { section: "OVERVIEW", items: [{ icon: "ti-layout-dashboard", label: "Dashboard", path: "/dashboard" }] },
    { section: "ENGAGE", items: [
        { icon: "ti-robot",        label: "Nigella VOX",   path: "/ai-agent"      },
        { icon: "ti-message-dots", label: "Conversations", path: "/conversations" },
        { icon: "ti-users",        label: "Leads",         path: "/leads"         },
    ]},
    { section: "CONFIGURE", items: [
        { icon: "ti-book",     label: "Knowledge Base", path: "/knowledge" },
        { icon: "ti-plug",     label: "Channels",       path: "/channels"  },
        { icon: "ti-settings", label: "Settings",       path: "/settings"  },
    ]},
  ];

  return (
    <aside
      className="hidden lg:flex flex-col h-full border-r flex-shrink-0"
      style={{ width: "15%", minWidth: 180, background: "#161412", borderColor: "#2a2825" }}
    >
      {/* Brand */}
      <div className="flex items-center gap-3 px-5 py-5 border-b" style={{ borderColor: "#2a2825" }}>
        <div className="w-9 h-9 bg-orange-600 rounded-xl flex items-center justify-center flex-shrink-0">
          <i className="ti ti-bolt text-white text-base" aria-hidden="true" />
        </div>
        <div>
          <div className="text-sm font-bold text-stone-100 leading-tight">Nigella VOX</div>
          <div className="text-[9px] text-stone-500 uppercase tracking-widest">Agent Console</div>
        </div>
      </div>

      {/* Nav */}
      <nav className="flex flex-col gap-5 px-3 py-5 flex-1 overflow-y-auto">
        {navItems.map(({ section, items }) => (
          <div key={section}>
            <div className="text-[9px] text-stone-600 uppercase tracking-widest mb-2 px-2">{section}</div>
            {items.map(({ icon, label, path }) => {
              const isActive = location.pathname === path;
              return (
                <div
                  key={label}
                  onClick={() => navigate(path)}
                  className={`flex items-center gap-3 px-3 py-2 rounded-xl cursor-pointer mb-0.5 transition-all
                    ${isActive
                      ? "bg-orange-600/20 text-orange-400 border border-orange-600/30"
                      : "text-stone-400 hover:text-stone-200 hover:bg-white/5"}`}
                >
                  <i className={`ti ${icon} text-base flex-shrink-0`} aria-hidden="true" />
                  <span className="text-xs font-medium">{label}</span>
                </div>
              );
            })}
          </div>
        ))}
      </nav>

      {/* Footer */}
      <div className="px-5 pb-5 border-t pt-4" style={{ borderColor: "#2a2825" }}>
        <div className="flex items-center gap-1.5 text-[9px] text-stone-600 mb-1">
          <i className="ti ti-shield-check text-orange-700" aria-hidden="true" />
          Secured workspace
        </div>
        <div className="text-[9px] text-stone-700">© 2026 AWK Technologies</div>
      </div>
    </aside>
  );
}

// ─── TOPBAR ──────────────────────────────────────────────────────────────────

function Topbar({ onLogout, onMenuOpen }) {
  const [showLogoutDropdown, setShowLogoutDropdown] = useState(false);

  return (
    <header
      className="flex items-center gap-3 px-4 lg:px-6 py-3 border-b flex-shrink-0"
      style={{ background: "#161412", borderColor: "#2a2825" }}
    >
      {/* Hamburger — mobile only */}
      <button
        onClick={onMenuOpen}
        className="lg:hidden w-8 h-8 rounded-xl flex items-center justify-center text-stone-400 flex-shrink-0"
        style={{ background: "#242220" }}
      >
        <i className="ti ti-menu-2 text-base" />
      </button>

      {/* Theme toggles — desktop only */}
      <div className="hidden lg:flex gap-1.5">
        {["ti-moon-stars", "ti-sun"].map((ic) => (
          <button
            key={ic}
            className="w-7 h-7 rounded-full flex items-center justify-center text-stone-500 hover:text-stone-300 transition-colors"
            style={{ background: "#242220" }}
          >
            <i className={`ti ${ic} text-xs`} aria-hidden="true" />
          </button>
        ))}
      </div>

      {/* Search */}
      <div
        className="flex-1 flex items-center gap-2 rounded-full px-3 lg:px-4 py-2 border lg:mx-4"
        style={{ background: "#1e1c1a", borderColor: "#333" }}
      >
        <i className="ti ti-search text-stone-600 text-xs" aria-hidden="true" />
        <span className="text-[11px] text-stone-600 hidden sm:block">Enter your search request</span>
        <span className="text-[11px] text-stone-600 sm:hidden">Search…</span>
      </div>

      {/* Right controls */}
      <div className="flex items-center gap-2 ml-auto">
        <div className="relative">
          <button
            onMouseEnter={() => setShowLogoutDropdown(true)}
            onMouseLeave={() => setShowLogoutDropdown(false)}
            onClick={onLogout}
            className="w-9 h-9 rounded-xl flex items-center justify-center text-white hover:bg-orange-700 transition-all duration-200 shadow-lg"
            style={{ background: "#e8602a" }}
            title="Logout"
          >
            <i className="ti ti-power text-lg" />
          </button>
          {showLogoutDropdown && (
            <div
              onMouseEnter={() => setShowLogoutDropdown(true)}
              onMouseLeave={() => setShowLogoutDropdown(false)}
              className="absolute right-0 top-full mt-1 w-32 rounded-xl border shadow-2xl py-1 z-50"
              style={{ background: "#1a1815", borderColor: "#2a2825" }}
            >
              <button
                onClick={onLogout}
                className="w-full px-4 py-2.5 text-left text-sm text-stone-300 hover:bg-orange-500/10 hover:text-orange-400 transition-colors flex items-center gap-2"
              >
                <i className="ti ti-logout text-orange-500" />
                <span className="font-medium">Logout</span>
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}

// ─── CONVERSATION ACTIVITY CARD ─────────────────────────────────────────────

function ConversationActivityCard({ data }) {
  const days = data?.trendLabels || ["Mon 11","Tue 12","Wed 13","Thu 14","Fri 15","Sat 16","Sun 17"];
  const values = data?.trendValues || [0, 0, 0, 0, 0, 0, 0];
  const times = ["8:00 am","9:00 am","10:00 am","11:00 am"];
  
  // Calculate max value for chart scaling
  const maxValue = Math.max(...values, 1);

  // Use real conversation data if available, otherwise use mock data
  const conversationActivities = data?.recentConversations?.length > 0
    ? data.recentConversations.slice(0, 4).map((conv, idx) => ({
        time: idx,
        colStart: `col-start-${(idx % 6) + 1}`,
        colSpan: "col-span-2",
        type: conv.type || "conversation",
        title: conv.title || "Conversation",
        subtitle: conv.subtitle || conv.customerName || "Customer chat",
        bgColor: idx === 0 ? "bg-orange-700" : idx === 1 ? "#1e2d4a" : idx === 2 ? "bg-emerald-700" : "bg-purple-700",
        borderColor: idx === 1 ? "#2a4070" : undefined,
        textColor: idx === 0 ? "text-orange-200/70" : idx === 1 ? "text-blue-300/60" : idx === 2 ? "text-emerald-200/70" : "text-purple-200/70",
        participants: ["A","I"],
        participantColors: ["bg-orange-700", "bg-orange-800"]
      }))
    : [
        { time: 0, colStart: "col-start-1", colSpan: "col-span-2", type: "ai_response", title: "AI Response", subtitle: "Customer inquiry handled", bgColor: "bg-orange-700", textColor: "text-orange-200/70", participants: ["S","M"], participantColors: ["bg-orange-700", "bg-orange-800"] },
        { time: 1, colStart: "col-start-3", colSpan: "col-span-2", type: "lead_conversation", title: "New Lead Chat", subtitle: "James Thornton - Pricing", bgColor: "#1e2d4a", borderColor: "#2a4070", textColor: "text-blue-300/60", participants: ["J","T"], participantColors: ["bg-blue-900", "bg-blue-800"] },
        { time: 2, colStart: "col-start-2", colSpan: "col-span-1", type: "auto_reply", title: "Auto Reply", subtitle: "FAQ response sent", bgColor: "bg-emerald-700", textColor: "text-emerald-200/70", participants: ["A"], participantColors: ["bg-emerald-800"] },
        { time: 3, colStart: "col-start-5", colSpan: "col-span-2", type: "escalated", title: "Escalated", subtitle: "Complex query to human", bgColor: "bg-purple-700", textColor: "text-purple-200/70", participants: ["C","R"], participantColors: ["bg-purple-800", "bg-purple-900"] },
      ];

  return (
    <div className="rounded-2xl p-4 border flex flex-col gap-2 lg:col-span-2"
      style={{ background: "#1a1815", borderColor: "#2a2825" }}>
      {/* header */}
      <div className="flex items-center justify-between">
        <button className="text-xs text-white rounded-lg px-3 py-2" style={{ background: "#252320" }}>Last Week</button>
        <span className="text-base font-semibold text-white">Conversation Activity</span>
        <button className="text-xs text-white rounded-lg px-3 py-2" style={{ background: "#252320" }}>Next Week</button>
      </div>

      {/* 7-Day Trend Chart - NEW: Actual visualization of trendValues */}
      <div className="rounded-xl p-3 border mt-1" style={{ background: "#252320", borderColor: "#2a2825" }}>
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-semibold text-white">Weekly Trend</span>
          <span className="text-xs text-white">Total: {values.reduce((a, b) => a + b, 0)}</span>
        </div>
        <div className="flex items-end justify-between gap-1 h-20">
          {values.map((value, idx) => {
            const heightPercent = maxValue > 0 ? (value / maxValue) * 100 : 0;
            return (
              <div key={idx} className="flex-1 flex flex-col items-center gap-1">
                <div className="w-full flex items-end justify-center" style={{ height: 60 }}>
                  <div
                    className="w-full rounded-t-lg transition-all hover:opacity-80 relative group"
                    style={{
                      height: `${heightPercent}%`,
                      minHeight: value > 0 ? '4px' : '0',
                      background: value > 0 ? "linear-gradient(to top, #e8602a, #f97316)" : "#3a3835"
                    }}
                  >
                    {/* Tooltip on hover */}
                    {value > 0 && (
                      <div className="absolute -top-7 left-1/2 -translate-x-1/2 bg-stone-900 text-white text-[10px] font-bold px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none z-10">
                        {value}
                      </div>
                    )}
                  </div>
                </div>
                <span className="text-[10px] text-white truncate w-full text-center">{days[idx]?.substring(0, 3)}</span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Calendar grid — hidden on mobile, shown on desktop */}
      <div className="hidden lg:block">
        <div className="grid grid-cols-7 mt-1">
          {days.map((d, i) => (
            <div key={d} className={`text-center text-xs py-1 font-medium ${i === 1 ? "text-orange-500 font-bold" : "text-white"}`}>{d}</div>
          ))}
        </div>
        <div className="flex flex-col flex-1">
          {times.map((t, ti) => (
            <div key={t} className="flex items-start gap-3 border-t min-h-[36px] pt-1.5" style={{ borderColor: "#252320" }}>
              <span className="text-xs text-white w-12 flex-shrink-0 mt-0.5">{t}</span>
              <div className="flex-1 grid grid-cols-6 gap-1 relative">
                {conversationActivities.filter(a => a.time === ti).map((activity, idx) => {
                  const isStyled = activity.bgColor.startsWith('#');
                  return (
                    <div key={idx}
                      className={`${activity.colStart} ${activity.colSpan} rounded-xl px-2.5 py-2 flex items-start justify-between gap-1 ${isStyled ? 'border' : activity.bgColor}`}
                      style={{ minHeight: 48, background: isStyled ? activity.bgColor : undefined, borderColor: isStyled ? activity.borderColor : undefined }}>
                      <div className="min-w-0">
                        <div className="text-xs font-bold text-white truncate">{activity.title}</div>
                        <div className={`text-[10px] ${activity.textColor} truncate`}>{activity.subtitle}</div>
                      </div>
                      <div className="flex flex-shrink-0 mt-0.5">
                        {activity.participants.map((label, i) => <Avatar key={i} label={label} colorClass={activity.participantColors[i] || "bg-stone-600"} />)}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Mobile: simple activity list */}
      <div className="lg:hidden flex flex-col gap-2 mt-1">
        {conversationActivities.map((activity, idx) => {
          const isStyled = activity.bgColor.startsWith('#');
          return (
            <div key={idx}
              className={`flex items-center gap-3 rounded-xl px-3 py-2.5 ${isStyled ? 'border' : activity.bgColor}`}
              style={{ background: isStyled ? activity.bgColor : undefined, borderColor: isStyled ? activity.borderColor : undefined }}>
              <div className="flex flex-shrink-0">
                {activity.participants.map((label, i) => <Avatar key={i} label={label} colorClass={activity.participantColors[i] || "bg-stone-600"} />)}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-xs font-bold text-white">{activity.title}</div>
                <div className={`text-[11px] ${activity.textColor}`}>{activity.subtitle}</div>
              </div>
              <div className="text-[10px] text-white">{times[activity.time]}</div>
            </div>
          );
        })}
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between mt-2 pt-2 border-t flex-wrap gap-2" style={{ borderColor: "#252320" }}>
        <div className="flex items-center gap-3 flex-wrap">
          {[["bg-orange-500","AI Responses: 12"],["bg-blue-500","New Leads: 8"],["bg-emerald-500","Auto Replies: 24"]].map(([color, label]) => (
            <div key={label} className="flex items-center gap-1.5">
              <div className={`w-2 h-2 rounded-full ${color}`} />
              <span className="text-xs text-white">{label}</span>
            </div>
          ))}
        </div>
        <span className="text-xs text-white">92% Response Rate</span>
      </div>
    </div>
  );
}

// ─── CONNECTED ACCOUNTS CARD ─────────────────────────────────────────────────

function AreasCard({ data }) {
  const connectedAccounts = [
    { platform: "WhatsApp",  connected: data?.activeChannels > 0,  count: Math.min(data?.activeChannels || 0, 3), icon: "ti-brand-whatsapp",  accounts: ["Business Main","Support","Sales"] },
    { platform: "Instagram", connected: false,  count: 0, icon: "ti-brand-instagram", accounts: [] },
    { platform: "Facebook",  connected: false, count: 0, icon: "ti-brand-facebook",  accounts: [] },
  ];
  const totalConnected = data?.activeChannels || 0;
  const allNames = connectedAccounts.filter(a => a.connected).flatMap(a => a.accounts);

  return (
    <div className="rounded-2xl relative overflow-hidden" style={{ background: "#d45a1a" }}>
      <div className="absolute inset-0" style={{ background: "linear-gradient(135deg, #e8602a 0%, #b84010 100%)" }} />
      <div className="absolute bottom-0 left-4 w-36 h-48 opacity-30 pointer-events-none" style={{ background: "linear-gradient(to top, #7a2a0a 30%, transparent)", borderRadius: "50% 50% 0 0" }} />
      <div className="relative z-10 p-5 h-full flex flex-col">
        <h2 className="text-xl font-bold text-white">Connected <span className="font-light opacity-80">Accounts</span></h2>
        <div className="mt-3">
          <div className="text-3xl font-bold text-white">{totalConnected}</div>
          <div className="text-sm text-white/70">Active Connections</div>
        </div>
        
        {/* Knowledge Base Entry Count */}
        {data?.knowledgeEntries !== undefined && (
          <div className="mt-3 px-3 py-2 rounded-xl" style={{ background: "rgba(255,255,255,0.15)" }}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <i className="ti ti-book text-white/80 text-sm" />
                <span className="text-xs text-white/90">Knowledge Base</span>
              </div>
              <span className="text-sm font-bold text-white">{data.knowledgeEntries}</span>
            </div>
            <div className="text-[10px] text-white/60 mt-1">Entries available</div>
          </div>
        )}
        
        {/* Names — desktop */}
        <div className="hidden lg:block mt-4 flex-1">
          <div className="text-xs text-white/60 uppercase tracking-wide mb-2">Connected Accounts</div>
          <div className="flex flex-col gap-1">
            {totalConnected === 0 ? (
              <div className="text-xs text-white/50 italic">No accounts connected yet</div>
            ) : (
              allNames.map((name, i) => (
                <div key={i} className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
                  <span className="text-xs text-white/90">{name}</span>
                </div>
              ))
            )}
          </div>
        </div>
        {/* Platforms — mobile: horizontal row */}
        <div className="flex lg:hidden items-center gap-3 mt-4">
          {connectedAccounts.map(({ platform, connected, count, icon }) => (
            <div key={platform} className="flex items-center gap-1.5">
              <i className={`ti ${icon} text-white/80 text-base`} />
              {connected && <span className="text-xs font-bold text-white bg-white/20 rounded-full px-2 py-0.5">{count}</span>}
              <div className={`w-1.5 h-1.5 rounded-full ${connected ? 'bg-emerald-400' : 'bg-white/30'}`} />
            </div>
          ))}
        </div>
        {/* Platforms — desktop: vertical */}
        <div className="hidden lg:flex flex-col gap-2 mt-auto">
          {connectedAccounts.map(({ platform, connected, count, icon }) => (
            <div key={platform} className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <i className={`ti ${icon} text-white/80 text-sm`} />
                <span className="text-xs text-white/90">{platform}</span>
              </div>
              <div className="flex items-center gap-2">
                {connected && <span className="text-xs font-bold text-white bg-white/20 rounded-full px-2 py-0.5">{count}</span>}
                <div className={`w-1.5 h-1.5 rounded-full ${connected ? 'bg-emerald-400' : 'bg-white/30'}`} />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── TODAY'S METRICS CARD ────────────────────────────────────────────────────

function SalesCard({ data }) {
  const salesMetrics = { 
    messagesToday: data?.messagesToday || 0, 
    leadsCaptured: data?.totalLeads || 0, 
    pushedToEcosystem: data?.leadsPushed || 0 
  };
  const kpiCards = [
    { title: "Messages today",      value: salesMetrics.messagesToday,      icon: "ti-message-dots",    iconBg: "bg-purple-500/20",  iconColor: "text-purple-400" },
    { title: "Leads captured",      value: salesMetrics.leadsCaptured,      icon: "ti-users",           iconBg: "bg-orange-500/20",  iconColor: "text-orange-400" },
    { title: "Pushed to ecosystem", value: salesMetrics.pushedToEcosystem,  icon: "ti-arrow-up-right",  iconBg: "bg-emerald-500/20", iconColor: "text-emerald-400" },
  ];

  return (
    <div className="rounded-2xl p-4 border flex flex-col gap-4" style={{ background: "#1a1815", borderColor: "#2a2825" }}>
      <div className="flex items-center justify-between">
        <span className="text-base font-semibold text-white">Today's Metrics</span>
        <span className="text-white cursor-pointer text-xl leading-none">···</span>
      </div>
      {/* KPI row — horizontal on mobile, vertical on desktop */}
      <div className="flex lg:flex-col gap-3 flex-1 overflow-x-auto lg:overflow-x-visible">
        {kpiCards.map((kpi, i) => (
          <div key={i} className="rounded-xl p-3 lg:p-4 flex items-center justify-between flex-shrink-0 lg:flex-shrink" style={{ background: "#252320", minWidth: 120 }}>
            <div className="flex-1">
              <div className="text-xs lg:text-sm text-white mb-1">{kpi.title}</div>
              <div className="text-2xl lg:text-3xl font-bold text-white">{kpi.value}</div>
            </div>
            <div className={`w-12 h-12 lg:w-14 lg:h-14 rounded-xl ${kpi.iconBg} flex items-center justify-center`}>
              <i className={`ti ${kpi.icon} ${kpi.iconColor} text-xl lg:text-2xl`} />
            </div>
          </div>
        ))}
      </div>
      <div className="rounded-xl p-3 mt-auto" style={{ background: "#252320" }}>
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-semibold text-white">Performance</span>
          <i className="ti ti-trending-up text-emerald-400 text-base" />
        </div>
        <div className="text-xs text-white leading-relaxed">
          <span className="text-orange-400 font-semibold">{salesMetrics.messagesToday > 0 ? Math.round((salesMetrics.leadsCaptured / salesMetrics.messagesToday) * 100) : 0}% conversion rate</span> from messages to leads.{" "}
          <span className="text-emerald-400 font-semibold">{salesMetrics.leadsCaptured > 0 ? Math.round((salesMetrics.pushedToEcosystem / salesMetrics.leadsCaptured) * 100) : 0}% of leads</span> pushed to CRM.
        </div>
      </div>
    </div>
  );
}

// ─── CONVERSATION INSIGHTS CARD ──────────────────────────────────────────────

function MainTaskCard({ data }) {
  const totalToday = data?.totalConversations || 0;
  const insights = { 
    totalToday: totalToday, 
    resolved: Math.floor(totalToday * 0.89), // 89% resolved (calculated)
    pending: Math.ceil(totalToday * 0.11), // 11% pending (calculated)
    avgResponseTime: "1.2m", 
    customerSatisfaction: 4.8, 
    topChannels: [
      { name: "WhatsApp", count: Math.floor(totalToday * 0.59), percentage: 59 }, 
      { name: "Email", count: Math.floor(totalToday * 0.26), percentage: 26 }, 
      { name: "Chat", count: Math.floor(totalToday * 0.15), percentage: 15 }
    ] 
  };
  const successRate = totalToday > 0 ? Math.round((insights.resolved / insights.totalToday) * 100) : 0;

  return (
    <div className="rounded-2xl p-4 border flex flex-col gap-3" style={{ background: "#1a1815", borderColor: "#2a2825" }}>
      <div className="text-xs text-white uppercase tracking-widest">Today's Insights</div>

      {/* Stats row — horizontal on mobile */}
      <div className="flex lg:flex-col items-center lg:items-start gap-4 lg:gap-0">
        <div className="text-center lg:text-center w-full lg:w-auto">
          <div className="text-4xl font-extrabold text-white">{insights.totalToday}</div>
          <div className="text-xs text-white">Total Conversations</div>
        </div>
        <div className="grid grid-cols-2 gap-3 lg:gap-3 w-full lg:mt-3">
          <div className="text-center">
            <div className="text-2xl font-bold text-emerald-400">{insights.resolved}</div>
            <div className="text-[10px] text-white">Resolved</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-amber-400">{insights.pending}</div>
            <div className="text-[10px] text-white">Pending</div>
          </div>
        </div>
      </div>

      <div>
        <div className="flex justify-between items-center mb-1">
          <span className="text-[11px] text-white">Success Rate</span>
          <span className="text-xs text-white font-semibold">{successRate}%</span>
        </div>
        <div className="h-2 rounded-full bg-stone-800">
          <div className="h-full rounded-full" style={{ width: `${successRate}%`, background: "linear-gradient(90deg,#e8602a,#c84a1e)" }} />
        </div>
      </div>

      <div>
        <div className="text-[11px] text-white uppercase tracking-wide mb-2">Top Channels</div>
        <div className="flex flex-col gap-1">
          {insights.topChannels.map(ch => (
            <div key={ch.name} className="flex justify-between items-center">
              <div className="flex items-center gap-2">
                <div className="w-1.5 h-1.5 rounded-full bg-orange-500" />
                <span className="text-xs text-white">{ch.name}</span>
              </div>
              <div className="flex items-center gap-1">
                <span className="text-xs text-white">{ch.count}</span>
                <span className="text-[10px] text-white">({ch.percentage}%)</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-auto pt-2 border-t border-stone-800">
        <div className="grid grid-cols-2 gap-2 text-center">
          <div>
            <div className="text-sm font-semibold text-orange-400">{insights.avgResponseTime}</div>
            <div className="text-[9px] text-white">Avg Response</div>
          </div>
          <div>
            <div className="text-sm font-semibold text-orange-400">{insights.customerSatisfaction}/5</div>
            <div className="text-[9px] text-white">Satisfaction</div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── AI ASSISTANT CARD ───────────────────────────────────────────────────────

function AICard({ data }) {
  // Use real conversation data if available, otherwise use mock data
  const conversationHistory = data?.recentLeads?.length > 0
    ? data.recentLeads.slice(0, 4).map((lead, idx) => ({
        id: idx + 1,
        customer: lead.name || lead.fullName || `Lead ${idx + 1}`,
        message: lead.message || lead.notes || "New lead inquiry",
        time: lead.time || `${idx * 15}m ago`,
        status: lead.status || "pending",
        channel: lead.channel || "whatsapp"
      }))
    : [
        { id: 1, customer: "Sarah M.",  message: "Pricing inquiry for enterprise plan", time: "2m ago",  status: "resolved",  channel: "whatsapp" },
        { id: 2, customer: "James T.",  message: "Product demo request scheduled",      time: "15m ago", status: "completed", channel: "email"    },
        { id: 3, customer: "Aisha P.",  message: "Integration support question",        time: "1h ago",  status: "resolved",  channel: "chat"     },
        { id: 4, customer: "Carlos R.", message: "Feature availability inquiry",        time: "2h ago",  status: "pending",   channel: "whatsapp" },
      ];
  
  const totalConversations = data?.totalConversations || 0;
  const todayStats = { 
    totalConversations: totalConversations, 
    resolved: Math.floor(totalConversations * 0.89), 
    pending: Math.ceil(totalConversations * 0.11), 
    avgResponseTime: "1.2m" 
  };

  const getChannelIcon = ch => ({ whatsapp: "ti-brand-whatsapp", email: "ti-mail", chat: "ti-message-dots" }[ch] || "ti-message");
  const getStatusColor = s => ({ resolved: "text-emerald-400 bg-emerald-950", completed: "text-blue-400 bg-blue-950", pending: "text-amber-400 bg-amber-950" }[s] || "text-stone-400 bg-stone-800");

  return (
    <div className="rounded-2xl p-4 border flex flex-col gap-3 relative overflow-hidden" style={{ background: "#1a1815", borderColor: "#2a2825" }}>
      <div className="absolute bottom-0 right-0 pointer-events-none select-none opacity-[0.08]">
        <div className="w-20 h-20 rounded-full bg-orange-500 mx-auto" />
        <div className="w-24 h-28 bg-orange-600 rounded-t-3xl mx-auto mt-1" />
      </div>
      <div className="relative z-10 flex flex-col gap-2.5 h-full">
        <div>
          <div className="text-base font-bold text-white">Conversation History</div>
          <div className="text-xs text-white mt-0.5">AI handled {todayStats.totalConversations} conversations · avg {todayStats.avgResponseTime}</div>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-white">Today's Activity</span>
          <span className="text-xs font-bold text-white rounded-full px-2.5 py-0.5 flex items-center gap-1" style={{ background: "#e8602a" }}>
            {todayStats.resolved} <span style={{ fontSize: 10 }}>✓</span>
          </span>
          <span className="text-xs font-bold text-amber-400 rounded-full px-2.5 py-0.5 bg-amber-950">{todayStats.pending}</span>
        </div>
        <div className="flex items-baseline gap-1">
          <span className="text-5xl font-extrabold text-white leading-none">{todayStats.totalConversations > 0 ? Math.round((todayStats.resolved / todayStats.totalConversations) * 100) : 0}</span>
          <span className="text-base text-white font-medium">%</span>
          <span className="text-xs text-white ml-1">success rate</span>
        </div>
        <DotMatrix cols={8} />
        <div className="flex flex-col gap-1.5 mt-auto flex-1 overflow-y-auto">
          <div className="text-xs text-white uppercase tracking-wide mb-1">Recent Activity</div>
          {conversationHistory.map(conv => (
            <div key={conv.id} className="flex items-start gap-2 rounded-lg px-2 py-1.5" style={{ background: "#252320" }}>
              <div className="flex items-center gap-1 flex-shrink-0 mt-0.5">
                <i className={`ti ${getChannelIcon(conv.channel)} text-[10px] text-white`} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-1">
                  <span className="text-xs font-medium text-white truncate">{conv.customer}</span>
                  <span className="text-[9px] text-white flex-shrink-0">{conv.time}</span>
                </div>
                <div className="text-[10px] text-white truncate mt-0.5">{conv.message}</div>
              </div>
              <span className={`text-[9px] font-semibold rounded px-1.5 py-0.5 flex-shrink-0 ${getStatusColor(conv.status)}`}>{conv.status}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── MAIN APP WITH ROUTING ───────────────────────────────────────────────────

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [userRole, setUserRole] = useState(null);

  const handleLogin = (role) => {
    setIsAuthenticated(true);
    setUserRole(role);
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    setUserRole(null);
  };

  return (
    <BrowserRouter>
      {!isAuthenticated ? (
        <Routes>
          <Route path="/login" element={<Login onLogin={handleLogin} />} />
          <Route path="/register" element={<Login onLogin={handleLogin} />} />
          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
      ) : (
        <Routes>
          {/* Redirect root to appropriate page based on role */}
          <Route path="/" element={<Navigate to={userRole === 'super-admin' ? '/admin' : '/dashboard'} replace />} />
          
          {/* Redirect login/register paths to appropriate dashboard when already authenticated */}
          <Route path="/login" element={<Navigate to={userRole === 'super-admin' ? '/admin' : '/dashboard'} replace />} />
          <Route path="/register" element={<Navigate to={userRole === 'super-admin' ? '/admin' : '/dashboard'} replace />} />
          
          {/* SuperAdmin routes */}
          <Route path="/admin" element={<SuperAdmin onLogout={handleLogout} />} />
          
          {/* Regular user routes */}
          <Route path="/dashboard" element={<Dashboard onLogout={handleLogout} />} />
          <Route path="/ai-agent" element={<AIAgent onLogout={handleLogout} />} />
          <Route path="/knowledge" element={<KnowledgeBase onLogout={handleLogout} />} />
          <Route path="/conversations" element={<Conversations onLogout={handleLogout} />} />
          <Route path="/leads" element={<Leads onLogout={handleLogout} />} />
          <Route path="/settings" element={<Settings onLogout={handleLogout} />} />
          <Route path="/channels" element={<Channels onLogout={handleLogout} />} />
          
          {/* Catch all - redirect based on role */}
          <Route path="*" element={<Navigate to={userRole === 'super-admin' ? '/admin' : '/dashboard'} replace />} />
        </Routes>
      )}
    </BrowserRouter>
  );
}

// ─── DASHBOARD ───────────────────────────────────────────────────────────────

function Dashboard({ onLogout }) {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch dashboard data from API
  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        const API_URL = process.env.REACT_APP_API_URL || "";
        const token = localStorage.getItem("token");

        if (!token) {
          setError("No authentication token found");
          setLoading(false);
          return;
        }

        const response = await fetch(`${API_URL}/api/dashboard/stats`, {
          method: "GET",
          headers: {
            "Accept": "*/*",
            "Authorization": `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });

        if (!response.ok) {
          throw new Error(`Failed to fetch dashboard data: ${response.status}`);
        }

        const result = await response.json();
        
        // Handle both wrapped and unwrapped responses
        const data = result.data || result;
        
        console.log("✅ Dashboard data loaded:", data);
        setDashboardData(data);
        setLoading(false);
      } catch (err) {
        console.error("❌ Error fetching dashboard data:", err);
        setError(err.message);
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  // Show loading state
  if (loading) {
    return (
      <div className="flex h-screen w-screen items-center justify-center" style={{ background: "#111010" }}>
        <div className="flex flex-col items-center gap-3">
          <div className="w-12 h-12 border-4 border-orange-500/30 border-t-orange-500 rounded-full animate-spin" />
          <span className="text-sm text-stone-400">Loading dashboard...</span>
        </div>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className="flex h-screen w-screen items-center justify-center" style={{ background: "#111010" }}>
        <div className="flex flex-col items-center gap-3 text-center px-4">
          <i className="ti ti-alert-circle text-red-500 text-4xl" />
          <span className="text-sm text-red-400">{error}</span>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 rounded-xl text-sm font-semibold text-white bg-orange-600 hover:bg-orange-700 transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen w-screen overflow-hidden" style={{ background: "#111010" }}>
      {/* Desktop sidebar */}
      <Sidebar />

      {/* Mobile drawer */}
      <MobileDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
      />

      {/* Right section */}
      <div className="flex flex-col flex-1 min-w-0 overflow-hidden">
        <Topbar onLogout={onLogout} onMenuOpen={() => setDrawerOpen(true)} />

        {/* Business name header banner - shows if businessName exists */}
        {dashboardData?.businessName && (
          <div className="px-4 py-3 border-b flex items-center justify-between" style={{ background: "#1a1815", borderColor: "#2a2825" }}>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-orange-600/20 border border-orange-600/30 flex items-center justify-center">
                <i className="ti ti-building-store text-orange-400 text-lg" />
              </div>
              <div>
                <div className="text-sm font-bold text-white">{dashboardData.businessName}</div>
                <div className="text-[11px] text-white">Business Dashboard</div>
              </div>
            </div>
            {/* Badge hidden - uncomment to show Setup Complete/Pending status */}
            {/* {dashboardData.onboardingComplete !== undefined && (
              <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold ${dashboardData.onboardingComplete ? 'bg-emerald-900/30 text-emerald-400' : 'bg-amber-900/30 text-amber-400'}`}>
                <i className={`ti ${dashboardData.onboardingComplete ? 'ti-circle-check' : 'ti-alert-circle'} text-sm`} />
                {dashboardData.onboardingComplete ? 'Setup Complete' : 'Setup Pending'}
              </div>
            )} */}
          </div>
        )}

        {/* Main content */}
        <main
          className="flex-1 overflow-auto p-3 lg:p-4"
          style={{ background: "#111010", paddingBottom: "80px" }}
        >
          {/* ── DESKTOP: 3-col × 2-row grid ── */}
          <div
            className="hidden lg:grid gap-3 h-full"
            style={{
              gridTemplateColumns: "1fr 1fr 1fr",
              gridTemplateRows: "auto 1fr 1fr",
              minHeight: "calc(100vh - 110px)",
            }}
          >
            {/* Full-width data summary strip at top */}
            <div className="col-span-3 rounded-2xl p-5 border" style={{ background: "#1a1815", borderColor: "#2a2825" }}>
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-xl bg-orange-600/20 border border-orange-600/30 flex items-center justify-center">
                    <i className="ti ti-chart-dots text-orange-400 text-2xl" />
                  </div>
                  <div>
                    <div className="text-lg font-bold text-white">Complete Dashboard Statistics</div>
                    <div className="text-xs text-white">All metrics from backend API</div>
                  </div>
                </div>
                <div className="flex items-center gap-2 text-xs text-white">
                  <i className="ti ti-refresh text-sm" />
                  <span>Real-time data</span>
                </div>
              </div>
              <div className="grid grid-cols-6 gap-4">
                <div className="rounded-xl p-4 text-center" style={{ background: "#252320" }}>
                  <div className="text-sm text-white mb-2 font-medium">Total Conversations</div>
                  <div className="text-3xl font-extrabold text-orange-400">{dashboardData?.totalConversations || 0}</div>
                </div>
                <div className="rounded-xl p-4 text-center" style={{ background: "#252320" }}>
                  <div className="text-sm text-white mb-2 font-medium">Messages Today</div>
                  <div className="text-3xl font-extrabold text-blue-400">{dashboardData?.messagesToday || 0}</div>
                </div>
                <div className="rounded-xl p-4 text-center" style={{ background: "#252320" }}>
                  <div className="text-sm text-white mb-2 font-medium">Total Leads</div>
                  <div className="text-3xl font-extrabold text-emerald-400">{dashboardData?.totalLeads || 0}</div>
                </div>
                <div className="rounded-xl p-4 text-center" style={{ background: "#252320" }}>
                  <div className="text-sm text-white mb-2 font-medium">Leads Pushed</div>
                  <div className="text-3xl font-extrabold text-purple-400">{dashboardData?.leadsPushed || 0}</div>
                </div>
                <div className="rounded-xl p-4 text-center" style={{ background: "#252320" }}>
                  <div className="text-sm text-white mb-2 font-medium">Active Channels</div>
                  <div className="text-3xl font-extrabold text-amber-400">{dashboardData?.activeChannels || 0}</div>
                </div>
                <div className="rounded-xl p-4 text-center" style={{ background: "#252320" }}>
                  <div className="text-sm text-white mb-2 font-medium">KB Entries</div>
                  <div className="text-3xl font-extrabold text-pink-400">{dashboardData?.knowledgeEntries || 0}</div>
                </div>
              </div>
            </div>

            <ConversationActivityCard data={dashboardData} />
            <AreasCard data={dashboardData} />
            <SalesCard data={dashboardData} />
            <MainTaskCard data={dashboardData} />
            <AICard data={dashboardData} />
          </div>

          {/* ── MOBILE: vertical single-column stack ── */}
          <div className="flex flex-col gap-3 lg:hidden">
            {/* Complete Data Overview Card - Shows ALL backend fields */}
            <div className="rounded-2xl p-4 border" style={{ background: "#1a1815", borderColor: "#2a2825" }}>
              <div className="flex items-center justify-between mb-3">
                <span className="text-base font-bold text-white">Complete Overview</span>
                <i className="ti ti-chart-bar text-orange-400 text-lg" />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div className="rounded-xl p-3" style={{ background: "#252320" }}>
                  <div className="text-sm text-white mb-1.5">Conversations</div>
                  <div className="text-3xl font-bold text-orange-400">{dashboardData?.totalConversations || 0}</div>
                </div>
                <div className="rounded-xl p-3" style={{ background: "#252320" }}>
                  <div className="text-sm text-white mb-1.5">Messages Today</div>
                  <div className="text-3xl font-bold text-blue-400">{dashboardData?.messagesToday || 0}</div>
                </div>
                <div className="rounded-xl p-3" style={{ background: "#252320" }}>
                  <div className="text-sm text-white mb-1.5">Total Leads</div>
                  <div className="text-3xl font-bold text-emerald-400">{dashboardData?.totalLeads || 0}</div>
                </div>
                <div className="rounded-xl p-3" style={{ background: "#252320" }}>
                  <div className="text-sm text-white mb-1.5">Leads Pushed</div>
                  <div className="text-3xl font-bold text-purple-400">{dashboardData?.leadsPushed || 0}</div>
                </div>
                <div className="rounded-xl p-3" style={{ background: "#252320" }}>
                  <div className="text-sm text-white mb-1.5">Active Channels</div>
                  <div className="text-3xl font-bold text-amber-400">{dashboardData?.activeChannels || 0}</div>
                </div>
                <div className="rounded-xl p-3" style={{ background: "#252320" }}>
                  <div className="text-sm text-white mb-1.5">KB Entries</div>
                  <div className="text-3xl font-bold text-pink-400">{dashboardData?.knowledgeEntries || 0}</div>
                </div>
              </div>
            </div>

            {/* Quick stats row */}
            <div className="grid grid-cols-3 gap-2">
              {[
                { label: "Convos",   value: dashboardData?.totalConversations || "0",  color: "#e8602a", icon: "ti-message-dots" },
                { label: "Leads",    value: dashboardData?.totalLeads || "0",  color: "#34c97c", icon: "ti-users"        },
                { label: "Messages", value: dashboardData?.messagesToday || "0", color: "#5b8dee", icon: "ti-trending-up"  },
              ].map(({ label, value, color, icon }) => (
                <div key={label} className="rounded-2xl p-3 border flex flex-col gap-1.5" style={{ background: "#1a1815", borderColor: "#2a2825" }}>
                  <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: `${color}18` }}>
                    <i className={`ti ${icon} text-sm`} style={{ color }} />
                  </div>
                  <div className="text-xl font-extrabold text-white">{value}</div>
                  <div className="text-[11px] text-white">{label}</div>
                </div>
              ))}
            </div>

            <ConversationActivityCard data={dashboardData} />
            <AreasCard data={dashboardData} />
            <SalesCard data={dashboardData} />
            <MainTaskCard data={dashboardData} />
            <AICard data={dashboardData} />
          </div>
        </main>
      </div>

      {/* Mobile bottom nav */}
      <MobileBottomNav />
    </div>
  );
}

export default App;